import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, EyeOff, GripVertical, ChevronUp, ChevronDown, Snowflake } from 'lucide-react';

const ColumnSettingsModal = ({ isOpen, onClose, columns, columnOrder, hiddenColumns, frozenColumns, onApply }) => {
  const [localOrder, setLocalOrder] = useState([]);
  const [localHidden, setLocalHidden] = useState(new Set());
  const [localFrozen, setLocalFrozen] = useState(new Set());
  const [draggedIndex, setDraggedIndex] = useState(null);
  const draggedIndexRef = useRef(null);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Use provided order or default to column keys
      const order = columnOrder?.length > 0
        ? columnOrder
        : columns.map(c => c.key);
      setLocalOrder(order);
      setLocalHidden(new Set(hiddenColumns || []));
      setLocalFrozen(new Set(frozenColumns || []));
    }
  }, [isOpen, columns, columnOrder, hiddenColumns, frozenColumns]);

  if (!isOpen) return null;

  // Get column label by key — prefer settingsLabel (longer, context-rich) so the
  // Modify View list stays readable when the grid uses short duplicate labels
  // like "AI"/"AO" under section-grouped headers.
  const getColumnLabel = (key) => {
    const col = columns.find(c => c.key === key);
    return col?.settingsLabel || col?.label || key;
  };

  // Toggle column visibility
  const toggleVisibility = (key) => {
    setLocalHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Toggle column freeze
  const toggleFrozen = (key) => {
    setLocalFrozen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Move column up
  const moveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...localOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalOrder(newOrder);
  };

  // Move column down
  const moveDown = (index) => {
    if (index === localOrder.length - 1) return;
    const newOrder = [...localOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalOrder(newOrder);
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    draggedIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const fromIdx = draggedIndexRef.current;
    if (fromIdx === null || fromIdx === index) return;

    const newOrder = [...localOrder];
    const draggedItem = newOrder[fromIdx];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(index, 0, draggedItem);
    setLocalOrder(newOrder);
    setDraggedIndex(index);
    draggedIndexRef.current = index;
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    draggedIndexRef.current = null;
  };

  // Apply changes
  const handleApply = () => {
    onApply({
      columnOrder: localOrder,
      hiddenColumns: Array.from(localHidden),
      frozenColumns: Array.from(localFrozen)
    });
    onClose();
  };

  // Reset to defaults
  const handleReset = () => {
    const defaultOrder = columns.map(c => c.key);
    setLocalOrder(defaultOrder);
    setLocalHidden(new Set());
    setLocalFrozen(new Set(defaultOrder.length > 0 ? [defaultOrder[0]] : []));
  };

  return (
    <div className="stui-modal-overlay" onClick={onClose}>
      <div className="stui-modal" onClick={e => e.stopPropagation()}>
        <div className="stui-modal-header">
          <h3>Modify View</h3>
          <button className="stui-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="stui-modal-body">
          <p className="column-settings-hint">Drag to reorder · eye to show/hide · snowflake to freeze</p>
          <div className="column-settings-list">
            {localOrder.map((key, index) => {
              const isHidden = localHidden.has(key);
              const isFrozen = localFrozen.has(key);
              return (
                <div
                  key={key}
                  className={`column-settings-item ${isHidden ? 'hidden-column' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="column-drag-handle">
                    <GripVertical size={16} />
                  </div>
                  <span className="column-label">{getColumnLabel(key)}</span>
                  <div className="column-actions">
                    <button
                      className="column-move-btn"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      className="column-move-btn"
                      onClick={() => moveDown(index)}
                      disabled={index === localOrder.length - 1}
                      title="Move down"
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      className={`column-freeze-btn ${isFrozen ? 'is-frozen' : ''}`}
                      onClick={() => toggleFrozen(key)}
                      title={isFrozen ? 'Unfreeze column' : 'Freeze column'}
                    >
                      <Snowflake size={16} />
                    </button>
                    <button
                      className={`column-visibility-btn ${isHidden ? 'is-hidden' : ''}`}
                      onClick={() => toggleVisibility(key)}
                      title={isHidden ? 'Show column' : 'Hide column'}
                    >
                      {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="stui-modal-footer stui-modal-footer-split">
          <button className="stui-btn-ghost" onClick={handleReset}>
            Reset
          </button>
          <div className="stui-row-md">
            <button className="stui-btn stui-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="stui-btn stui-btn-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnSettingsModal;
